import { useRef, KeyboardEvent } from 'react'

/**
 * Custom hook for keyboard navigation in scrollable regions
 * Provides standard keyboard interactions for accessibility:
 * - Arrow Up/Down: Scroll by 40px
 * - Page Up/Down: Scroll by 90% of visible height
 * - Ctrl+Home: Scroll to top
 * - Ctrl+End: Scroll to bottom
 *
 * @returns {Object} Object containing:
 *   - scrollRef: Ref to attach to the scrollable container
 *   - handleKeyDown: Event handler for keyboard navigation
 *
 * @example
 * ```tsx
 * const { scrollRef, handleKeyDown } = useScrollableKeyboardNavigation()
 *
 * return (
 *   <div
 *     ref={scrollRef}
 *     tabIndex={0}
 *     role="region"
 *     aria-label="Scrollable content"
 *     onKeyDown={handleKeyDown}
 *   >
 *     {content}
 *   </div>
 * )
 * ```
 */
const useScrollableKeyboardNavigation = () => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const element = scrollRef.current
    if (!element) return

    const scrollAmount = 40 // pixels to scroll per arrow key press
    const pageScrollAmount = Math.floor(element.clientHeight * 0.9) // 90% of visible height

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        element.scrollTop += scrollAmount
        break
      case 'ArrowUp':
        event.preventDefault()
        element.scrollTop -= scrollAmount
        break
      case 'PageDown':
        event.preventDefault()
        element.scrollTop += pageScrollAmount
        break
      case 'PageUp':
        event.preventDefault()
        element.scrollTop -= pageScrollAmount
        break
      case 'Home':
        if (event.ctrlKey) {
          event.preventDefault()
          element.scrollTop = 0
        }
        break
      case 'End':
        if (event.ctrlKey) {
          event.preventDefault()
          element.scrollTop = element.scrollHeight
        }
        break
      default:
        // Don't prevent default for other keys
        break
    }
  }

  return { scrollRef, handleKeyDown }
}

export default useScrollableKeyboardNavigation
